import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  createInsight,
  generateInsightSlug,
} from "@/lib/api/insights.functions";
import { getBlockTemplates, generateInsightDraft } from "@/lib/api/insights-generator.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { InsightsAdminNav } from "@/components/admin/InsightsAdminNav";
import { SlugField } from "@/components/admin/SlugField";
import { ContentAdminTabs } from "@/components/admin/ContentAdminTabs";
import { BlockDocEditor } from "@/components/admin/doc/BlockDocEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import type { ArticleBlock } from "@/lib/types/content";
import { MediaField } from "@/components/admin/MediaPicker";
import type { MediaSelection } from "@/lib/media/types";
import { getPublishedLeaders } from "@/lib/api/public.functions";
import { listInsightTagsAdmin } from "@/lib/api/insight-tags.functions";
import { INSIGHT_FILTER_CATEGORIES } from "@/lib/insights/categories";
import { InsightTagPicker } from "@/components/admin/InsightTagPicker";
import { articleBlocksToDoc } from "@/lib/doc/doc-convert";
import { emptyEditorDoc, type EditorDoc } from "@/lib/doc/doc-types";

function toDateInputValue(value: Date | string | null | undefined): string {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

export const Route = createFileRoute("/admin/insights/new")({
  loader: async () => {
    const [templates, publishedLeaders, tagCatalog] = await Promise.all([
      getBlockTemplates(),
      getPublishedLeaders(),
      listInsightTagsAdmin(),
    ]);
    return {
      templates,
      publishedLeaders,
      tagOptions: tagCatalog
        .filter((tag) => tag.status === "published")
        .map((tag) => ({ name: tag.name, featured: tag.featured })),
    };
  },
  component: NewInsightPage,
});

function NewInsightPage() {
  const { templates, publishedLeaders, tagOptions } = Route.useLoaderData();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [category, setCategory] = useState("Thought Leadership");
  const [tags, setTags] = useState<string[]>([]);
  const [excerpt, setExcerpt] = useState("");
  const [authorSlug, setAuthorSlug] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [authorDesignation, setAuthorDesignation] = useState("");
  const [status, setStatus] = useState<"draft" | "published">("draft");
  const [publishedAt, setPublishedAt] = useState("");
  const [editorDoc, setEditorDoc] = useState<EditorDoc>(emptyEditorDoc());
  const [editorKey, setEditorKey] = useState(0);
  const [cover, setCover] = useState<MediaSelection>({});
  const [ogImage, setOgImage] = useState<MediaSelection>({});
  const [saving, setSaving] = useState(false);

  function applyArticleTemplate(templateBlocks: ArticleBlock[], successMessage: string) {
    setEditorDoc(articleBlocksToDoc(templateBlocks));
    setEditorKey((key) => key + 1);
    toast.success(successMessage);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const row = await createInsight({
        data: {
          title,
          slug,
          category,
          tags,
          excerpt,
          status,
          publishedAt: publishedAt || undefined,
          editorDoc,
          coverMediaId: cover.mediaId,
          coverUrl: cover.url,
          ogImageMediaId: ogImage.mediaId,
          authorSlug: authorSlug || undefined,
          authorName: authorName || undefined,
          authorDesignation: authorDesignation || undefined,
        },
      });
      toast.success("Insight created");
      navigate({ to: "/admin/insights/$id", params: { id: row.id } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleAiDraft() {
    const draft = await generateInsightDraft({ data: { topic: title || "Malaysia tax update", category } });
    setTitle(draft.title);
    setSlug(draft.suggestedSlug);
    setExcerpt(draft.excerpt);
    setEditorDoc(draft.editorDoc ?? articleBlocksToDoc(draft.blocks));
    setEditorKey((key) => key + 1);
    toast.success("Draft generated — review in the Content tab");
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="New insight" />
      <InsightsAdminNav />
      <ContentAdminTabs
        settingsLabel="Insight settings"
        settingsForm={
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <SlugField title={title} slug={slug} onSlugChange={setSlug} onGenerate={() => generateInsightSlug({ data: { title } })} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INSIGHT_FILTER_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as "draft" | "published")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Publish date</Label>
              <Input
                type="date"
                value={publishedAt}
                onChange={(e) => setPublishedAt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Shown on the public site. Leave empty to use today when publishing.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Topic tags</Label>
              <InsightTagPicker options={tagOptions} value={tags} onChange={setTags} />
            </div>
            <div className="space-y-2">
              <Label>Excerpt</Label>
              <Textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Author</Label>
              <Select
                value={authorSlug || "__none__"}
                onValueChange={(v) => {
                  if (v === "__none__") {
                    setAuthorSlug("");
                    setAuthorName("");
                    setAuthorDesignation("");
                    return;
                  }
                  const leader = publishedLeaders.find((l) => l.slug === v);
                  if (leader) {
                    setAuthorSlug(leader.slug);
                    setAuthorName(leader.name);
                    setAuthorDesignation(leader.designation);
                  }
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select author" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No author</SelectItem>
                  {publishedLeaders.map((l) => (
                    <SelectItem key={l.slug} value={l.slug}>{l.name} — {l.designation}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <MediaField label="Cover image" value={cover} onChange={setCover} />
              <MediaField label="OG image" value={ogImage} onChange={setOgImage} />
            </div>
            <div className="flex flex-wrap gap-2">
              {templates.map((t) => (
                <Button
                  key={t.name}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => applyArticleTemplate(t.blocks, `${t.name} applied to editor`)}
                >
                  {t.name}
                </Button>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={handleAiDraft}>
                AI draft
              </Button>
            </div>
          </div>
        }
        content={
          <BlockDocEditor
            editorKey={`new-${editorKey}`}
            initialDoc={editorDoc}
            onChange={setEditorDoc}
            className="w-full"
          />
        }
      />
      <AdminFormFooter
        cancelTo="/admin/insights"
        onSave={handleSave}
        saving={saving}
        saveLabel="Create insight"
        savingLabel="Creating…"
      />
    </AdminPageShell>
  );
}
